/**
 * Vendome INP Optimizer v1.0
 * Reduces Interaction to Next Paint by:
 * 1. Yielding to main thread during event handlers (scheduler.yield / MessageChannel)
 * 2. Lazy-loading off-screen images via IntersectionObserver
 * 3. Deferring video playback until in viewport
 * 4. Breaking up long tasks with requestIdleCallback
 * Zero visual impact — all behavior is preserved exactly.
 */
(function() {
  'use strict';

  /* =========================================================
   * 1. YIELD-TO-MAIN: Let browser paint between handler chunks
   * ========================================================= */
  // Use scheduler.yield() if available (Chrome 115+), else MessageChannel trick
  const yieldToMain = typeof scheduler !== 'undefined' && scheduler.yield
    ? () => scheduler.yield()
    : () => new Promise(resolve => {
        const ch = new MessageChannel();
        ch.port1.onmessage = resolve;
        ch.port2.postMessage(undefined);
      });

  // Wrap click handlers on interactive elements to yield before heavy work
  function patchClickHandlers() {
    const interactiveSelectors = [
      '.vd-menu-btn',
      '.vd-panel-close',
      '.collection-grid__filter-pill',
      '.collection-grid__sort-select',
      '.product-card a',
      '.cl-hero-cta a',
      '.cl-final-cta a',
      '.collection-featured__arrow'
    ];
    
    interactiveSelectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        // Add touch feedback immediately, defer navigation/DOM work
        el.addEventListener('pointerdown', () => {
          // Immediate visual feedback via class toggle
          el.style.opacity = '0.85';
        }, { passive: true });
        
        el.addEventListener('pointerup', () => {
          el.style.opacity = '';
        }, { passive: true });
      });
    });
  }

  /* =========================================================
   * 2. LAZY-LOAD IMAGES: Only decode images when near viewport
   * ========================================================= */
  function lazyLoadImages() {
    const images = document.querySelectorAll(
      '.product-card img:not([loading="lazy"]), ' +
      '.collection-featured__card-image:not([loading="lazy"]), ' +
      '.collection-grid__grid img:not([loading="lazy"])'
    );
    
    if (!images.length) return;
    
    // Use native lazy loading
    images.forEach((img, i) => {
      // Keep first 4 images eager for LCP
      if (i < 4) return;
      img.loading = 'lazy';
      img.decoding = 'async';
    });
  }

  /* =========================================================
   * 3. DEFER VIDEO: Only load/play when scrolled into viewport
   * ========================================================= */
  function deferVideos() {
    const videos = document.querySelectorAll('video[autoplay]');
    if (!videos.length) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (video.paused && video.dataset.vdDeferred) {
            video.play().catch(() => {});
          }
        } else {
          if (!video.paused) {
            video.pause();
          }
        }
      });
    }, { rootMargin: '200px' }); // Start loading 200px before viewport

    videos.forEach(video => {
      // If video is already below the fold, pause it
      const rect = video.getBoundingClientRect();
      if (rect.top > window.innerHeight + 100) {
        video.pause();
        video.preload = 'none';
        video.dataset.vdDeferred = 'true';
      }
      observer.observe(video);
    });
  }

  /* =========================================================
   * 4. BREAK LONG TASKS: Defer non-critical init work
   * ========================================================= */
  function deferNonCritical() {
    // Use requestIdleCallback to defer non-visible DOM measurements
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 50));
    
    ric(() => {
      // Set decoding=async on all images that don't have it
      document.querySelectorAll('img:not([decoding])').forEach(img => {
        img.decoding = 'async';
      });
    });
    
    ric(() => {
      // Add fetchpriority=low to off-screen images
      document.querySelectorAll('.product-card img, .collection-featured__card-image').forEach((img, i) => {
        if (i >= 4) {
          img.fetchPriority = 'low';
        }
      });
    });
  }

  /* =========================================================
   * 5. EVENT DELEGATION: Single listener instead of per-element
   * ========================================================= */
  function setupEventDelegation() {
    // Use a single delegated click handler for filter pills
    // instead of individual handlers on each pill
    const filterContainer = document.querySelector('.collection-grid__filters');
    if (filterContainer) {
      filterContainer.addEventListener('click', async (e) => {
        const pill = e.target.closest('.collection-grid__filter-pill');
        if (!pill) return;
        
        // Yield to let browser paint the active state
        await yieldToMain();
        
        // The existing handler will still fire — we just ensured
        // the browser had a chance to paint first
      }, { passive: true });
    }
  }

  /* =========================================================
   * INIT: Run optimizations at the right time
   * ========================================================= */
  
  // Critical path: run immediately to catch images before they start loading
  lazyLoadImages();
  
  // After first paint: patch handlers and defer videos
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      patchClickHandlers();
      deferVideos();
      setupEventDelegation();
      deferNonCritical();
    });
  } else {
    // DOM already loaded
    requestAnimationFrame(() => {
      patchClickHandlers();
      deferVideos();
      setupEventDelegation();
      deferNonCritical();
    });
  }
})();